import { ClassConstructor, plainToInstance } from 'class-transformer';
import {
  validateSync,
  ValidationError,
  ValidationOptions,
} from 'class-validator';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import set from 'lodash/set';

export type TSetMessagesRecurseOptions = { fields?: string[] };
export type TCreateValidatorOptions = ValidationOptions &
  TSetMessagesRecurseOptions;

export const createValidator =
  (
    Class: ClassConstructor<object>,
    opts: TCreateValidatorOptions = { groups: ['error'] },
  ) =>
  ({ value }: { value: unknown }) => {
    const classInstance = plainToInstance<object, unknown>(Class, value);
    const errors = validateSync(classInstance, opts);
    const fieldErrors = setMessagesRecurse({}, errors, [], opts);

    // Transform errors into tanstack form format
    const adaptedErrors: Record<
      string,
      { errors?: string[]; meta?: { touched: boolean } }
    > = {};

    for (const [fieldPath, message] of Object.entries(fieldErrors)) {
      const fieldName = fieldPath.replace('__parent', '');
      if (!adaptedErrors[fieldName]) {
        adaptedErrors[fieldName] = {};
      }
      adaptedErrors[fieldName].errors = [message];
      adaptedErrors[fieldName].meta = { touched: false };
    }

    return Object.keys(adaptedErrors).length ? adaptedErrors : null;
  };

function setMessagesRecurse(
  result: Record<string, string>,
  errors: ValidationError[],
  path: string[] = [],
  opts: TSetMessagesRecurseOptions = {},
) {
  const { fields } = opts;
  errors.forEach((err) => {
    setMessagesRecurse(
      result,
      err.children || [],
      path.concat(err.property),
      opts,
    );
    if (err.constraints && err.property) {
      const fieldName = [...path, err.property].join('.');
      if (!fields || fields.includes(fieldName)) {
        set(
          result,
          [
            ...path,
            err.property +
              (isArray(err.value) || isPlainObject(err.value)
                ? '__parent'
                : ''),
          ],
          Object.values(err.constraints || {}).join(', '),
        );
      }
    }
  });
  return result;
}
